import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import { schedulesApi } from "@/api/schedules";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/empty-state";
import { formatRelative } from "@/lib/format";

export const Route = createFileRoute("/_authed/schedules")({
  component: SchedulesPage,
});

function SchedulesPage() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["schedules"],
    queryFn: () => schedulesApi.list(),
    refetchInterval: 30_000,
  });

  const schedules = q.data?.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-1 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          Monitor
        </div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Schedules</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Cron-triggered functions. Their runs stay here, out of the event-driven Runs list.
        </p>
      </div>

      <Card className="overflow-hidden">
        {q.isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
          </div>
        ) : schedules.length === 0 ? (
          <EmptyState
            icon={<CalendarClock size={32} />}
            title="No schedules yet"
            description="Define a function with a cron trigger and it will show up here."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5 font-mono text-[11px] uppercase tracking-wider">Function</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Schedule</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Next run</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Last run</TableHead>
                <TableHead className="pr-5 text-right font-mono text-[11px] uppercase tracking-wider">Runs · 24h</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedules.map((s) => (
                <TableRow
                  key={s.function_id}
                  className="group cursor-pointer"
                  onClick={() => navigate({ to: "/functions/$id", params: { id: s.function_id } })}
                >
                  <TableCell className="pl-5">
                    <Link
                      to="/functions/$id"
                      params={{ id: s.function_id }}
                      onClick={(e) => e.stopPropagation()}
                      className="font-mono text-[13px] font-medium text-foreground group-hover:text-brand"
                    >
                      {s.function_slug}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[12px] text-foreground/80">
                      {s.cron}
                    </code>
                  </TableCell>
                  <TableCell className="font-mono text-[13px] text-muted-foreground">
                    {s.next_run_at ? formatRelative(s.next_run_at) : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-[13px] text-muted-foreground">
                    {s.last_run_at ? formatRelative(s.last_run_at) : "never"}
                  </TableCell>
                  <TableCell className="pr-5 text-right font-mono text-[13px]">
                    <span className="text-muted-foreground">{s.runs_24h}</span>
                    {s.failed_24h > 0 && (
                      <span className="ml-2 text-fail">{s.failed_24h} failed</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
